import { Box, Card, Stack, SxProps } from '@mui/material';
import { BaseTheme } from 'design-system';
import { useEffect, useState } from 'react';
import useIsViewport from 'hooks/useIsViewport';
import HelpActions from './HelpActions';
import { ChipsInterface, SearchActionProps, SearchBarVariant, SearchSuggestion } from './SearchBar.types';
import SearchBarChips from './SearchBarChips';
import { useWhiteLabelling } from './WhiteLabellingProvider';

export type { SearchBarFieldProps } from './SearchBarField';
export type { SearchSuggestion } from './SearchBar.types';
export { SearchBarVariant, SearchAction } from './SearchBar.types';
export { DEFAULT_SEARCHBAR_WIDTH } from './SearchBar.utils';

export interface SearchbarProps {
  value?: string;
  placeholder?: string;
  onChange?: (text: string) => void;
  onClose?: () => void;
  chips?: ChipsInterface;
  loading?: boolean;
  action?: SearchActionProps;
  showHelp?: boolean;
  showClear?: boolean;
  sx?: SxProps<BaseTheme>;
  variant?: SearchBarVariant;
  alert?: string;
  selectedSuggestion?: SearchSuggestion | null;
  onSelectedSuggestionClear?: () => void;
  recentsStorageKey?: string;
  enhancedDropdown?: boolean;
}

const SearchBar = ({
  onClose,
  chips,
  action,
  showHelp = false,
  sx = {},
  alert,
  recentsStorageKey,
  enhancedDropdown,
  ...rest
}: SearchbarProps) => {
  const isMobile = useIsViewport('md');
  const [expandSection, setExpandSection] = useState(false);
  const { isFreshmarkProfile } = useWhiteLabelling();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const searchBarElement = document.getElementById('searchBarContainer');
      const searchChipsElement = document.getElementById('searchChipsBox');

      if (
        searchBarElement &&
        !searchBarElement.contains(event.target as Element) &&
        (!searchChipsElement || (searchChipsElement && !searchChipsElement.contains(event.target as Element)))
      ) {
        if (chips?.onClose) {
          chips.onClose();
        }
        onClose && onClose();
        setExpandSection(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [chips, onClose]);

  if (alert) {
    return <Card role={'alert'}>{alert}</Card>;
  }
  return (
    <Stack role="searchBarContainer" sx={sx} id="searchBarContainer">
      <SearchBarChips
        onClose={() => {}}
        expanded={expandSection}
        onSetExpanded={setExpandSection}
        chips={chips}
        recentsStorageKey={recentsStorageKey}
        enhancedDropdown={enhancedDropdown}
        action={
          action && {
            ...action,
            color: action.color ?? 'primary',
          }
        }
        {...rest}
      />
      {isMobile && showHelp && isFreshmarkProfile && (
        <Box
          sx={(theme) => ({
            marginTop: theme.spacing(1),
            marginRight: theme.spacing(1),
          })}
        >
          <HelpActions />
        </Box>
      )}
    </Stack>
  );
};

export default SearchBar;
